import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Button,
  Input,
  StyledModal,
  Dropdown,
} from "@bikdotai/bik-component-library";
import { DropdownOption } from "@bikdotai/bik-component-library/dist/esm/components/dropdown/type";
import {
  ServiceScreenFeature,
  Pod,
  Status,
  Availability,
  FeatureImportance,
  Product,
  TargetTeam,
  ChannelSupport,
} from "../../models/serviceFeatures/ServiceScreenFeatureModel";
import { ServiceScreenFeaturesService } from "../../services/ServiceScreenFeaturesService";
import FeatureImageUploadModal from "./FeatureImageUploadModal";
import VideoUploadModal from "../../../components/upload-modals/VideoUploadModal";
import CrossTagIcon from "../../../icons/crossTagIcon";
import InfoIcon from "../../../icons/infoIcon";
import { Tooltip } from "@bikdotai/bik-component-library";

interface FeatureFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feature: Partial<ServiceScreenFeature>) => Promise<void>;
  initialData?: ServiceScreenFeature;
  mode: "create" | "edit";
}

// Utility function to convert dd-mm-yyyy to yyyy-mm-dd for date input
const formatDateForInput = (dateStr: string): string => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 2) {
    // dd-mm-yyyy format
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

// Utility function to convert yyyy-mm-dd to dd-mm-yyyy
const formatDateForAPI = (dateStr: string): string => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    // yyyy-mm-dd format
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const FeatureForm: React.FC<FeatureFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  mode,
}) => {
  const [formData, setFormData] = useState<Partial<ServiceScreenFeature>>({
    feature_id: initialData?.feature_id,
    name: initialData?.name || "",
    short_description: initialData?.short_description || "",
    long_description: initialData?.long_description || "",
    pod: initialData?.pod || Pod.CAMPAIGN,
    goals: initialData?.goals || [],
    channels_supported: initialData?.channels_supported || [],
    status: initialData?.status || Status.Live,
    release_date: initialData?.release_date || "",
    availability: initialData?.availability || Availability.Available,
    enable: initialData?.enable ?? true,
    visible: initialData?.visible ?? false,
    enable_flow: initialData?.enable_flow || "",
    dependencies: initialData?.dependencies || "",
    similar_features: initialData?.similar_features || [],
    thumbnail_url: initialData?.thumbnail_url || "",
    banner_url: initialData?.banner_url || "",
    works_best_with_features: initialData?.works_best_with_features || [],
    help_doc_url: initialData?.help_doc_url || "",
    video_url: initialData?.video_url || "",
    faq: initialData?.faq || [],
    large_description: initialData?.large_description || "",
    key_benefits: initialData?.key_benefits || "",
    use_cases: initialData?.use_cases || [],
    steps_to_enable: initialData?.steps_to_enable || "",
    product: initialData?.product || Product.BIK,
    feature_importance: initialData?.feature_importance || FeatureImportance.M,
    is_ai: initialData?.is_ai ?? false,
    target_team: initialData?.target_team || TargetTeam.Marketing,
    is_popular: initialData?.is_popular ?? false,
    release_jet_feature_name: initialData?.release_jet_feature_name || "",
  });

  const [showThumbnailUpload, setShowThumbnailUpload] = useState(false);
  const [showBannerUpload, setShowBannerUpload] = useState(false);
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [releaseJetFeatures, setReleaseJetFeatures] = useState<string[]>([]);
  const [loadingReleaseJetFeatures, setLoadingReleaseJetFeatures] = useState(false);
  const [existingGoals, setExistingGoals] = useState<string[]>([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [newGoalInput, setNewGoalInput] = useState("");
  const [allFeatures, setAllFeatures] = useState<ServiceScreenFeature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  // Fetch release jet features on mount
  useEffect(() => {
    const fetchReleaseJetFeatures = async () => {
      setLoadingReleaseJetFeatures(true);
      try {
        const service = new ServiceScreenFeaturesService();
        const response = await service.getAllFeaturesInfo();
        const featureNames = response.data.map((item) => item.featureName);
        // Remove duplicates and sort
        const uniqueFeatures = Array.from(new Set(featureNames)).sort();
        setReleaseJetFeatures(uniqueFeatures);
      } catch (error) {
        console.error("Failed to fetch release jet features:", error);
        // Don't show error to user, just log it
      } finally {
        setLoadingReleaseJetFeatures(false);
      }
    };

    fetchReleaseJetFeatures();
  }, []);

  // Fetch all features to get existing goals and feature list for dropdowns
  useEffect(() => {
    const fetchFeaturesAndGoals = async () => {
      setLoadingFeatures(true);
      setLoadingGoals(true);
      try {
        const service = new ServiceScreenFeaturesService();
        const response = await service.getFeaturesList();
        const features = response.response.features;
        setAllFeatures(features);
        
        // Extract all goals from all features
        const allGoals: string[] = [];
        features.forEach((feature) => {
          if (feature.goals && Array.isArray(feature.goals)) {
            allGoals.push(...feature.goals);
          }
        });
        
        // Remove duplicates and sort
        const uniqueGoals = Array.from(new Set(allGoals)).sort();
        setExistingGoals(uniqueGoals);
      } catch (error) {
        console.error("Failed to fetch features and goals:", error);
        // Don't show error to user, just log it
      } finally {
        setLoadingFeatures(false);
        setLoadingGoals(false);
      }
    };

    if (isOpen) {
      fetchFeaturesAndGoals();
    }
  }, [isOpen]);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        release_date: formatDateForInput(initialData.release_date),
        visible: initialData.visible ?? false,
      });
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Feature ID validation: non-empty, no spaces anywhere (leading, trailing or
    // in-between), only letters, numbers and hyphens allowed. Validate the raw
    // value (no trimming) so leading/trailing spaces are caught too.
    if (!formData.feature_id?.trim()) {
      newErrors.feature_id = "Feature ID is required";
    } else if (/\s/.test(formData.feature_id)) {
      newErrors.feature_id = "Feature ID cannot contain spaces";
    } else if (!/^[a-zA-Z0-9-]+$/.test(formData.feature_id)) {
      newErrors.feature_id = "Feature ID can only contain letters, numbers and hyphens";
    }

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.short_description?.trim()) {
      newErrors.short_description = "Short description is required";
    }
    if (!formData.long_description?.trim()) {
      newErrors.long_description = "Long description is required";
    }
    if (!formData.channels_supported || formData.channels_supported.length === 0) {
      newErrors.channels_supported = "At least one channel is required";
    }
    if (!formData.release_date) {
      newErrors.release_date = "Release date is required";
    }
    if (!formData.thumbnail_url?.trim()) {
      newErrors.thumbnail_url = "Thumbnail URL is required";
    }
    if (!formData.large_description?.trim()) {
      newErrors.large_description = "Large description is required";
    }

    // Validate max 5 features for "Works Best With Features"
    if (
      formData.works_best_with_features &&
      formData.works_best_with_features.length > 5
    ) {
      newErrors.works_best_with_features = "Maximum 5 features allowed";
    }

    // Validate max 5 features for "Similar Features"
    if (formData.similar_features && formData.similar_features.length > 5) {
      newErrors.similar_features = "Maximum 5 features allowed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        release_date: formatDateForAPI(formData.release_date || ""),
      };
      await onSubmit(submitData);
      onClose();
    } catch (error: any) {
      alert(`Error: ${error.message || "Failed to save feature"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: keyof ServiceScreenFeature, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };


  const addFAQ = () => {
    const currentFAQ = formData.faq || [];
    updateField("faq", [...currentFAQ, { q: "", a: "" }]);
  };

  const updateFAQ = (index: number, field: "q" | "a", value: string) => {
    const currentFAQ = formData.faq || [];
    const updatedFAQ = [...currentFAQ];
    updatedFAQ[index] = { ...updatedFAQ[index], [field]: value };
    updateField("faq", updatedFAQ);
  };

  const removeFAQ = (index: number) => {
    const currentFAQ = formData.faq || [];
    updateField("faq", currentFAQ.filter((_, i) => i !== index));
  };

  const addUseCase = () => {
    const currentUseCases = formData.use_cases || [];
    updateField("use_cases", [...currentUseCases, { scenario: "", howItHelps: "" }]);
  };

  const updateUseCase = (index: number, field: "scenario" | "howItHelps", value: string) => {
    const currentUseCases = formData.use_cases || [];
    const updatedUseCases = [...currentUseCases];
    updatedUseCases[index] = { ...updatedUseCases[index], [field]: value };
    updateField("use_cases", updatedUseCases);
  };

  const removeUseCase = (index: number) => {
    const currentUseCases = formData.use_cases || [];
    updateField("use_cases", currentUseCases.filter((_, i) => i !== index));
  };

  // Dropdown options
  const podOptions: DropdownOption[] = Object.values(Pod).map((pod) => ({
    label: pod,
    value: pod,
    selected: formData.pod === pod,
  }));

  const statusOptions: DropdownOption[] = Object.values(Status)
    .filter((status) => status !== Status.ComingSoon)
    .map((status) => ({
      label: status,
      value: status,
      selected: formData.status === status,
    }));

  const importanceOptions: DropdownOption[] = Object.values(FeatureImportance).map(
    (importance) => ({
      label: importance.toUpperCase(),
      value: importance,
      selected: formData.feature_importance === importance,
    })
  );

  const productOptions: DropdownOption[] = Object.values(Product).map((product) => ({
    label: product,
    value: product,
    selected: formData.product === product,
  }));

  const targetTeamOptions: DropdownOption[] = Object.values(TargetTeam).map((team) => ({
    label: team.charAt(0).toUpperCase() + team.slice(1),
    value: team,
    selected: formData.target_team === team,
  }));

  const channelSupportOptions: DropdownOption[] = Object.values(ChannelSupport).map((channel) => {
    const labels: Record<ChannelSupport, string> = {
      [ChannelSupport.WhatsApp]: "WhatsApp",
      [ChannelSupport.SMS]: "SMS",
      [ChannelSupport.Email]: "Email",
      [ChannelSupport.RCS]: "RCS",
      [ChannelSupport.WebWidgets]: "Web widgets",
      [ChannelSupport.Instagram]: "Instagram",
    };
    return {
      label: labels[channel],
      value: channel,
      selected: formData.channels_supported?.includes(channel) || false,
    };
  });

  // Use only unique goals from existing features
  const businessGoalOptions: DropdownOption[] = existingGoals.map((goal) => {
    return {
      label: goal,
      value: goal,
      selected: formData.goals?.includes(goal) || false,
    };
  });

  const handleAddNewGoal = () => {
    const trimmedGoal = newGoalInput.trim();
    if (!trimmedGoal) return;
    
    // Check if goal already exists
    if (formData.goals?.includes(trimmedGoal)) {
      setNewGoalInput("");
      return;
    }
    
    // Add to goals list
    const updatedGoals = [...(formData.goals || []), trimmedGoal];
    updateField("goals", updatedGoals);
    
    // Add to existing goals if not already there
    if (!existingGoals.includes(trimmedGoal)) {
      setExistingGoals([...existingGoals, trimmedGoal].sort());
    }
    
    // Clear input
    setNewGoalInput("");
  };

  const releaseJetFeatureOptions: DropdownOption[] = releaseJetFeatures.map((featureName) => ({
    label: featureName,
    value: featureName,
    selected: formData.release_jet_feature_name === featureName,
  }));

  // Create feature options for dropdowns (exclude current feature in edit mode)
  const getFeatureOptions = (selectedFeatureIds: string[] = []): DropdownOption[] => {
    return allFeatures
      .filter((feature) => {
        // Exclude current feature in edit mode
        if (mode === "edit" && initialData?.feature_id === feature.feature_id) {
          return false;
        }
        return true;
      })
      .map((feature) => ({
        label: feature.name,
        value: feature.feature_id,
        selected: selectedFeatureIds.includes(feature.feature_id),
      }));
  };

  const worksBestWithOptions: DropdownOption[] = getFeatureOptions(
    formData.works_best_with_features || []
  );

  const similarFeaturesOptions: DropdownOption[] = getFeatureOptions(
    formData.similar_features || []
  );

  // Compute potential status based on release date (only when status is Live)
  const getPotentialStatus = (): string | null => {
    if (formData.status !== Status.Live || !formData.release_date) {
      return null;
    }

    try {
      // Convert release_date from yyyy-mm-dd (date input format) to Date object
      const releaseDateStr = formData.release_date;
      const [year, month, day] = releaseDateStr.split("-").map(Number);
      const releaseDate = new Date(year, month - 1, day);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      releaseDate.setHours(0, 0, 0, 0);

      // If release date is in the future
      if (releaseDate > currentDate) {
        return "coming soon";
      }

      // Calculate days difference
      const diffTime = Math.abs(currentDate.getTime() - releaseDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // If released within last 30 days
      if (diffDays < 30) {
        return "new";
      }

      // Otherwise it's live
      return "live";
    } catch {
      return null;
    }
  };

  const potentialStatus = getPotentialStatus();

  return (
    <StyledModal
      open={isOpen}
      headingTitle={mode === "create" ? "Create Feature" : "Edit Feature"}
      onClose={onClose}
      width="900px"
    >
      <div style={{ padding: "24px", maxHeight: "80vh", overflowY: "auto" }}>
        {/* Basic Information */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ marginBottom: "16px", fontSize: "16px", fontWeight: "600" }}>
            Basic Information
          </h3>

          {mode === "create" && (
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", fontSize: "14px" }}>
                <span>Feature ID</span>
                <Tooltip
                  body="Only letters, numbers and hyphens allowed"
                  placement="top"
                >
                  <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <InfoIcon width={16} height={16} color="#6b7280" />
                  </div>
                </Tooltip>
              </label>
              <Input
                value={formData.feature_id || ""}
                onChangeText={(value) => updateField("feature_id", value)}
                placeholder="Feature ID (letters, numbers and hyphens)"
              />
              {errors.feature_id && (
                <span style={{ color: "#dc2626", fontSize: "12px" }}>{errors.feature_id}</span>
              )}
            </div>
          )}

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Name
            </label>
            <Input
              value={formData.name || ""}
              onChangeText={(value) => updateField("name", value)}
              placeholder="Feature name"
            />
            {errors.name && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>{errors.name}</span>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Short Description (Not in the UI)
            </label>
            <Input
              value={formData.short_description || ""}
              onChangeText={(value) => updateField("short_description", value)}
              placeholder="Short description"
            />
            {errors.short_description && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>
                {errors.short_description}
              </span>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Long Description (Feature Card)
            </label>
            <textarea
              value={formData.long_description || ""}
              onChange={(e) => updateField("long_description", e.target.value)}
              placeholder="Long description"
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "8px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
              }}
            />
            {errors.long_description && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>
                {errors.long_description}
              </span>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Large Description (Details Page)
            </label>
            <ReactQuill
              theme="snow"
              value={formData.large_description || ""}
              onChange={(value) => updateField("large_description", value)}
              placeholder="Large description"
              modules={{
                toolbar: [
                  ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
                  [{ list: "ordered" }, { list: "bullet" }],
                ],
              }}
              style={{ background: "#fff" }}
            />
            {errors.large_description && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>
                {errors.large_description}
              </span>
            )}
          </div>
        </div>

        {/* Classification */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ marginBottom: "16px", fontSize: "16px", fontWeight: "600" }}>
            Classification
          </h3>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                Pod
              </label>
              <Dropdown
                options={podOptions}
                onSelect={(option: any) => {
                  if (Array.isArray(option)) {
                    const selected = option.find((o: any) => o.selected);
                    if (selected && selected.value) {
                      updateField("pod", selected.value as Pod);
                    }
                  } else if (option && option.selected && option.value) {
                    updateField("pod", option.value as Pod);
                  }
                }}
                width="100%"
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                Status
              </label>
              <Dropdown
                options={statusOptions}
                onSelect={(option: any) => {
                  if (Array.isArray(option)) {
                    const selected = option.find((o: any) => o.selected);
                    if (selected && selected.value) {
                      updateField("status", selected.value as Status);
                    }
                  } else if (option && option.selected && option.value) {
                    updateField("status", option.value as Status);
                  }
                }}
                width="100%"
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                Product
              </label>
              <Dropdown
                options={productOptions}
                onSelect={(option: any) => {
                  if (Array.isArray(option)) {
                    const selected = option.find((o: any) => o.selected);
                    if (selected && selected.value) {
                      updateField("product", selected.value as Product);
                    }
                  } else if (option && option.selected && option.value) {
                    updateField("product", option.value as Product);
                  }
                }}
                width="100%"
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                Target Team
              </label>
              <Dropdown
                options={targetTeamOptions}
                onSelect={(option: any) => {
                  if (Array.isArray(option)) {
                    const selected = option.find((o: any) => o.selected);
                    if (selected && selected.value) {
                      updateField("target_team", selected.value as TargetTeam);
                    }
                  } else if (option && option.selected && option.value) {
                    updateField("target_team", option.value as TargetTeam);
                  }
                }}
                width="100%"
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                Feature Importance
              </label>
              <Dropdown
                options={importanceOptions}
                onSelect={(option: any) => {
                  if (Array.isArray(option)) {
                    const selected = option.find((o: any) => o.selected);
                    if (selected && selected.value) {
                      updateField("feature_importance", selected.value as FeatureImportance);
                    }
                  } else if (option && option.selected && option.value) {
                    updateField("feature_importance", option.value as FeatureImportance);
                  }
                }}
                width="100%"
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                Release Date (dd-mm-yyyy)
                {potentialStatus && (
                  <span
                    style={{
                      marginLeft: "8px",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: "600",
                      backgroundColor:
                        potentialStatus === "coming soon"
                          ? "#fef3c7"
                          : potentialStatus === "new"
                          ? "#10b981"
                          : "#d1fae5",
                      color:
                        potentialStatus === "coming soon"
                          ? "#92400e"
                          : potentialStatus === "new"
                          ? "white"
                          : "#065f46",
                    }}
                  >
                    {potentialStatus.toUpperCase()}
                  </span>
                )}
              </label>
              <input
                type="date"
                value={formData.release_date || ""}
                onChange={(e) => updateField("release_date", e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              {errors.release_date && (
                <span style={{ color: "#dc2626", fontSize: "12px" }}>
                  {errors.release_date}
                </span>
              )}
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
                Release Jet Feature Name
              </label>
              <Dropdown
                options={releaseJetFeatureOptions}
                onSelect={(option: any) => {
                  if (Array.isArray(option)) {
                    const selected = option.find((o: any) => o.selected);
                    if (selected && selected.value) {
                      updateField("release_jet_feature_name", selected.value);
                    } else {
                      updateField("release_jet_feature_name", "");
                    }
                  } else if (option && option.selected && option.value) {
                    updateField("release_jet_feature_name", option.value);
                  } else {
                    updateField("release_jet_feature_name", "");
                  }
                }}
                width="100%"
                isSearchable={true}
                disabled={loadingReleaseJetFeatures}
              />
              {loadingReleaseJetFeatures && (
                <span style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  Loading features...
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Channels and Features */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ marginBottom: "16px", fontSize: "16px", fontWeight: "600" }}>
            Channels & Features
          </h3>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Channels Supported
            </label>
            <Dropdown
              options={channelSupportOptions}
              isMultiSelect={true}
              onSelect={(options: DropdownOption | DropdownOption[]) => {
                const optionsArray = Array.isArray(options) ? options : [options];
                const selectedChannels = optionsArray
                  .filter((o): o is DropdownOption & { selected: true; value: string } => {
                    const option = o as { selected?: boolean; value?: unknown };
                    return option.selected === true && typeof option.value === 'string';
                  })
                  .map((o) => o.value as ChannelSupport);
                updateField("channels_supported", selectedChannels);
              }}
              width="100%"
            />
            {errors.channels_supported && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>
                {errors.channels_supported}
              </span>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Goals
            </label>
            <Dropdown
              options={businessGoalOptions}
              isMultiSelect={true}
              disabled={loadingGoals}
              onSelect={(options: DropdownOption | DropdownOption[]) => {
                const optionsArray = Array.isArray(options) ? options : [options];
                const selectedGoals = optionsArray
                  .filter((o): o is DropdownOption & { selected: true; value: string } => {
                    const option = o as { selected?: boolean; value?: unknown };
                    return option.selected === true && typeof option.value === 'string';
                  })
                  .map((o) => o.value);
                updateField("goals", selectedGoals);
              }}
              width="100%"
            />
            <form
              style={{ marginTop: "8px", display: "flex", gap: "8px" }}
              onSubmit={(e) => {
                e.preventDefault();
                handleAddNewGoal();
              }}
            >
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Add new goal..."
                  value={newGoalInput}
                  onChangeText={(value) => setNewGoalInput(value)}
                />
              </div>
              <Button
                buttonText="Add"
                onClick={handleAddNewGoal}
                disabled={!newGoalInput.trim()}
                size="small"
                style={{
                  backgroundColor: "#731DCF",
                  color: "white",
                }}
              />
            </form>
            {loadingGoals && (
              <span style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Loading existing goals...
              </span>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Works Best With Features (max 5)
            </label>
            <Dropdown
              options={worksBestWithOptions}
              isMultiSelect={true}
              isSearchable={true}
              disabled={loadingFeatures}
              onSelect={(options: DropdownOption | DropdownOption[]) => {
                const optionsArray = Array.isArray(options) ? options : [options];
                const selectedFeatureIds = optionsArray
                  .filter((o): o is DropdownOption & { selected: true; value: string } => {
                    const option = o as { selected?: boolean; value?: unknown };
                    return option.selected === true && typeof option.value === 'string';
                  })
                  .map((o) => o.value);
                updateField("works_best_with_features", selectedFeatureIds);
              }}
              width="100%"
            />
            {loadingFeatures && (
              <span style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Loading features...
              </span>
            )}
            {errors.works_best_with_features && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>
                {errors.works_best_with_features}
              </span>
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Similar Features (max 5)
            </label>
            <Dropdown
              options={similarFeaturesOptions}
              isMultiSelect={true}
              isSearchable={true}
              disabled={loadingFeatures}
              onSelect={(options: DropdownOption | DropdownOption[]) => {
                const optionsArray = Array.isArray(options) ? options : [options];
                const selectedFeatureIds = optionsArray
                  .filter((o): o is DropdownOption & { selected: true; value: string } => {
                    const option = o as { selected?: boolean; value?: unknown };
                    return option.selected === true && typeof option.value === 'string';
                  })
                  .map((o) => o.value);
                updateField("similar_features", selectedFeatureIds);
              }}
              width="100%"
            />
            {loadingFeatures && (
              <span style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Loading features...
              </span>
            )}
            {errors.similar_features && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>
                {errors.similar_features}
              </span>
            )}
          </div>
        </div>

        {/* Images */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ marginBottom: "16px", fontSize: "16px", fontWeight: "600" }}>
            Images
          </h3>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Thumbnail URL
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={formData.thumbnail_url || ""}
                  onChangeText={(value) => updateField("thumbnail_url", value)}
                  placeholder="Thumbnail URL"
                />
              </div>
              <Button
                buttonText="Upload"
                onClick={() => setShowThumbnailUpload(true)}
                size="medium"
              />
            </div>
            {errors.thumbnail_url && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>
                {errors.thumbnail_url}
              </span>
            )}
            {formData.thumbnail_url && (
              <img
                src={formData.thumbnail_url}
                alt="Thumbnail preview"
                style={{
                  marginTop: "8px",
                  maxWidth: "200px",
                  maxHeight: "150px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                }}
              />
            )}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", fontSize: "14px" }}>
              <span>Banner URL</span>
              <Tooltip
                body="Recommended dimension: 1536x768px"
                placement="top"
              >
                <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                  <InfoIcon width={16} height={16} color="#6b7280" />
                </div>
              </Tooltip>
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={formData.banner_url || ""}
                  onChangeText={(value) => updateField("banner_url", value)}
                  placeholder="Banner URL"
                />
              </div>
              <Button
                buttonText="Upload"
                onClick={() => setShowBannerUpload(true)}
                size="medium"
              />
            </div>
            {errors.banner_url && (
              <span style={{ color: "#dc2626", fontSize: "12px" }}>
                {errors.banner_url}
              </span>
            )}
            {formData.banner_url && (
              <img
                src={formData.banner_url}
                alt="Banner preview"
                style={{
                  marginTop: "8px",
                  maxWidth: "400px",
                  maxHeight: "200px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                }}
              />
            )}
          </div>
        </div>

        {/* URLs and Additional Info */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ marginBottom: "16px", fontSize: "16px", fontWeight: "600" }}>
            URLs & Additional Information
          </h3>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Enable Flow (URL or "manual")
            </label>
            <Input
              value={formData.enable_flow || ""}
              onChangeText={(value) => updateField("enable_flow", value)}
              placeholder="https://app.bik.ai/settings/enable-feature or manual"
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Help Doc URL
            </label>
            <Input
              value={formData.help_doc_url || ""}
              onChangeText={(value) => updateField("help_doc_url", value)}
              placeholder="https://docs.bik.ai/feature"
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Video URL
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <div style={{ flex: 1 }}>
                <Input
                  value={formData.video_url || ""}
                  onChangeText={(value) => updateField("video_url", value)}
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <Button
                buttonText="Upload"
                onClick={() => setShowVideoUpload(true)}
                size="medium"
              />
            </div>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Dependencies
            </label>
            <textarea
              value={formData.dependencies || ""}
              onChange={(e) => updateField("dependencies", e.target.value)}
              placeholder="Feature dependencies"
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "8px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
              }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Key Benefits
            </label>
            <textarea
              value={formData.key_benefits || ""}
              onChange={(e) => updateField("key_benefits", e.target.value)}
              placeholder="Key benefits of this feature"
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "8px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
              }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontSize: "14px" }}>
              Steps to Enable
            </label>
            <textarea
              value={formData.steps_to_enable || ""}
              onChange={(e) => updateField("steps_to_enable", e.target.value)}
              placeholder="Steps to enable this feature"
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "8px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
              }}
            />
          </div>
        </div>

        {/* FAQ */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "600" }}>FAQ</h3>
            <Button
              buttonText="Add FAQ"
              onClick={addFAQ}
              size="small"
              style={{ backgroundColor: "#731DCF", color: "white" }}
            />
          </div>
          {formData.faq?.map((faq, index) => (
            <div
              key={index}
              style={{
                marginBottom: "16px",
                padding: "16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontWeight: "500" }}>FAQ {index + 1}</span>
                <button
                  onClick={() => removeFAQ(index)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#dc2626",
                  }}
                >
                  <CrossTagIcon width="16" height="16" />
                </button>
              </div>
              <div style={{ marginBottom: "8px" }}>
                <Input
                  value={faq.q}
                  onChangeText={(value) => updateFAQ(index, "q", value)}
                  placeholder="Question"
                />
              </div>
              <div>
                <textarea
                  value={faq.a}
                  onChange={(e) => updateFAQ(index, "a", e.target.value)}
                  placeholder="Answer"
                  style={{
                    width: "100%",
                    minHeight: "60px",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Use Cases */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "600" }}>Use Cases</h3>
            <Button
              buttonText="Add Use Case"
              onClick={addUseCase}
              size="small"
              style={{ backgroundColor: "#731DCF", color: "white" }}
            />
          </div>
          {formData.use_cases?.map((useCase, index) => (
            <div
              key={index}
              style={{
                marginBottom: "16px",
                padding: "16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontWeight: "500" }}>Use Case {index + 1}</span>
                <button
                  onClick={() => removeUseCase(index)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#dc2626",
                  }}
                >
                  <CrossTagIcon width="16" height="16" />
                </button>
              </div>
              <div style={{ marginBottom: "8px" }}>
                <Input
                  value={useCase.scenario}
                  onChangeText={(value) => updateUseCase(index, "scenario", value)}
                  placeholder="Scenario"
                />
              </div>
              <div>
                <textarea
                  value={useCase.howItHelps}
                  onChange={(e) => updateUseCase(index, "howItHelps", e.target.value)}
                  placeholder="How it helps"
                  style={{
                    width: "100%",
                    minHeight: "60px",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Boolean Flags */}
        <div style={{ marginBottom: "24px" }}>
          <h3 style={{ marginBottom: "16px", fontSize: "16px", fontWeight: "600" }}>
            Settings
          </h3>
          <div style={{ display: "flex", gap: "24px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={formData.enable ?? false}
                onChange={(e) => updateField("enable", e.target.checked)}
              />
              <span>Enable</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={formData.is_ai ?? false}
                onChange={(e) => updateField("is_ai", e.target.checked)}
              />
              <span>Is AI</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={formData.is_popular ?? false}
                onChange={(e) => updateField("is_popular", e.target.checked)}
              />
              <span>Is Popular</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={formData.visible ?? false}
                onChange={(e) => updateField("visible", e.target.checked)}
              />
              <span>Visible</span>
            </label>
          </div>
        </div>

        {/* Validation Summary */}
        {Object.keys(errors).length > 0 && (
          <p style={{ marginTop: "24px", marginBottom: 0, color: "#dc2626", fontSize: "15px" }}>
            Something is wrong
          </p>
        )}

        {/* Action Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "24px" }}>
          <Button
            buttonText="Cancel"
            onClick={onClose}
            size="medium"
            style={{
              color: "#374151",
              border: "1px solid #d1d5db",
            }}
          />
          <Button
            buttonText={isSubmitting ? "Saving..." : "Save"}
            onClick={handleSubmit}
            size="medium"
            disabled={isSubmitting}
            style={{
              backgroundColor: "#731DCF",
              color: "white",
            }}
          />
        </div>
      </div>

      {/* Image Upload Modals */}
      <FeatureImageUploadModal
        isOpen={showThumbnailUpload}
        onClose={() => setShowThumbnailUpload(false)}
        onImageUpload={(url) => {
          updateField("thumbnail_url", url);
          setShowThumbnailUpload(false);
        }}
        title="Upload Thumbnail"
      />

      <FeatureImageUploadModal
        isOpen={showBannerUpload}
        onClose={() => setShowBannerUpload(false)}
        onImageUpload={(url) => {
          updateField("banner_url", url);
          setShowBannerUpload(false);
        }}
        title="Upload Banner"
      />

      {/* Video Upload Modal */}
      <VideoUploadModal
        isOpen={showVideoUpload}
        onClose={() => setShowVideoUpload(false)}
        onVideoUpload={(url) => {
          updateField("video_url", url);
          setShowVideoUpload(false);
        }}
      />
    </StyledModal>
  );
};

export default FeatureForm;

